/**
 * Brand Portal Documentation Component
 * Comprehensive guide for brand owners on using the brand portal
 */

import React, { useState } from 'react';
import { ChevronRight, BookOpen, Package, TrendingUp, Eye, User, Users, HelpCircle, CheckCircle } from 'lucide-react';

export function BrandDocumentation() {
  const [expandedSection, setExpandedSection] = useState(null);

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const sections = [
    {
      id: 'overview',
      title: 'Getting Started',
      icon: BookOpen,
      content: (
        <div>
          <h3 style={docStyles.subtitle}>Welcome to the Brand Portal</h3>
          <p style={docStyles.paragraph}>
            The Yemberzal Brand Portal is your central hub for managing your brand presence on the platform. 
            Here you can add products, track performance, view analytics, and manage your brand profile.
          </p>
          
          <h4 style={docStyles.heading}>Key Features</h4>
          <ul style={docStyles.list}>
            <li><strong>Overview Dashboard:</strong> Get a quick snapshot of your brand's performance</li>
            <li><strong>Product Collection:</strong> Manage all your products in one place</li>
            <li><strong>Analytics:</strong> Track views, engagement, and category distribution</li>
            <li><strong>Insights:</strong> Deep dive into your brand's performance metrics</li>
            <li><strong>Profile Management:</strong> Update your brand information and settings</li>
          </ul>

          <h4 style={docStyles.heading}>Navigation</h4>
          <p style={docStyles.paragraph}>
            Use the sidebar on the left to navigate between different sections. The active section will be highlighted 
            in white. You can also access your profile and logout from the bottom of the sidebar.
          </p>
        </div>
      )
    },
    {
      id: 'products',
      title: 'Managing Products',
      icon: Package,
      content: (
        <div>
          <h3 style={docStyles.subtitle}>Adding New Products</h3>
          <ol style={docStyles.orderedList}>
            <li>Navigate to <strong>Collection</strong> from the sidebar</li>
            <li>Click the <strong>"Add New Product"</strong> button</li>
            <li>Fill in the product details:
              <ul style={docStyles.nestedList}>
                <li><strong>Title:</strong> Product name (required)</li>
                <li><strong>Price:</strong> Product price in your default currency</li>
                <li><strong>Description:</strong> Detailed product description</li>
                <li><strong>Image URL:</strong> Direct link to product image (must be publicly accessible)</li>
                <li><strong>Product Link:</strong> URL to your website or Instagram post</li>
                <li><strong>Link Type:</strong> Choose between Website or Instagram</li>
                <li><strong>Categories:</strong> Comma-separated list (e.g., "Handwoven, Shawl, Premium")</li>
              </ul>
            </li>
            <li>Click <strong>"Save Product"</strong> to add it to your collection</li>
          </ol>

          <h4 style={docStyles.heading}>Editing Products</h4>
          <ol style={docStyles.orderedList}>
            <li>Go to <strong>Collection</strong> to see all your products</li>
            <li>Click on any product card to edit it</li>
            <li>Update the fields you want to change</li>
            <li>Click <strong>"Update Product"</strong> to save changes</li>
          </ol>

          <h4 style={docStyles.heading}>Deleting Products</h4>
          <p style={docStyles.paragraph}>
            You can only delete products that you've manually added through the brand portal. 
            Scraped products (automatically imported) cannot be deleted but can be edited.
          </p>
          <ol style={docStyles.orderedList}>
            <li>Open the product you want to delete</li>
            <li>Scroll to the bottom of the edit form</li>
            <li>Click the <strong>"Delete Product"</strong> button</li>
            <li>Confirm the deletion</li>
          </ol>

          <div style={docStyles.tipBox}>
            <strong>💡 Tip:</strong> Use clear, high-quality product images. Images should be at least 800x800 pixels 
            for best display quality. Make sure your image URLs are publicly accessible (not behind authentication).
          </div>
        </div>
      )
    },
    {
      id: 'analytics',
      title: 'Understanding Analytics',
      icon: TrendingUp,
      content: (
        <div>
          <h3 style={docStyles.subtitle}>Analytics Dashboard</h3>
          <p style={docStyles.paragraph}>
            The Analytics section provides insights into how your products are performing on the platform.
          </p>

          <h4 style={docStyles.heading}>Key Metrics</h4>
          <ul style={docStyles.list}>
            <li><strong>Total Views:</strong> Number of times your products have been viewed</li>
            <li><strong>Product Distribution:</strong> Breakdown of views by category</li>
            <li><strong>Trending Products:</strong> Products with the highest engagement</li>
            <li><strong>Views per Product:</strong> Individual product performance</li>
          </ul>

          <h4 style={docStyles.heading}>Product Distribution by Category</h4>
          <p style={docStyles.paragraph}>
            This chart shows how views are distributed across your product categories. 
            This helps you understand which types of products resonate most with your audience.
          </p>

          <h4 style={docStyles.heading}>Interpreting the Data</h4>
          <ul style={docStyles.list}>
            <li>Higher view counts indicate products that are getting more attention</li>
            <li>Category distribution helps identify your most popular product types</li>
            <li>Trending percentages show relative performance compared to your other products</li>
          </ul>

          <div style={docStyles.tipBox}>
            <strong>💡 Tip:</strong> Regularly check your analytics to identify which products and categories 
            perform best. Use this data to inform your product strategy and marketing efforts.
          </div>
        </div>
      )
    },
    {
      id: 'insights',
      title: 'Brand Insights',
      icon: Eye,
      content: (
        <div>
          <h3 style={docStyles.subtitle}>Deep Dive into Performance</h3>
          <p style={docStyles.paragraph}>
            The Insights section provides detailed analytics and performance metrics for your brand.
          </p>

          <h4 style={docStyles.heading}>What You'll Find</h4>
          <ul style={docStyles.list}>
            <li>Detailed product performance metrics</li>
            <li>Engagement statistics</li>
            <li>Trending analysis</li>
            <li>Historical performance data</li>
          </ul>

          <h4 style={docStyles.heading}>Using Insights</h4>
          <p style={docStyles.paragraph}>
            Review your insights regularly to understand:
          </p>
          <ul style={docStyles.list}>
            <li>Which products are driving the most engagement</li>
            <li>Seasonal trends in your product performance</li>
            <li>Opportunities to optimize your product listings</li>
            <li>Areas where you can improve visibility</li>
          </ul>
        </div>
      )
    },
    {
      id: 'products',
      title: 'Managing Products',
      icon: Package,
      content: (
        <div>
          <h3 style={docStyles.subtitle}>Product Overview</h3>
          <p style={docStyles.paragraph}>
            Your product collection is displayed in a grid view where you can view details, edit, and manage your inventory.
          </p>

          <h4 style={docStyles.heading}>Viewing Product Details</h4>
          <ol style={docStyles.orderedList}>
            <li>Go to <strong>Collection</strong> to see all your products</li>
            <li>Click anywhere on a product card to view its details</li>
            <li>The detail view shows complete product information including price, description, categories, and performance stats</li>
            <li>Click the <strong>"Edit Product"</strong> button in the top-right to modify the product</li>
          </ol>

          <h4 style={docStyles.heading}>Quick Edit with Icons</h4>
          <p style={docStyles.paragraph}>
            Each product card has action icons in the top-right corner:
          </p>
          <ul style={docStyles.list}>
            <li><strong>✏️ Edit Icon:</strong> Click to edit product details (always visible)</li>
            <li><strong>🗑️ Delete Icon:</strong> Click to delete product (only for manually added products)</li>
          </ul>

          <h3 style={docStyles.subtitle}>Adding New Products</h3>
          <ol style={docStyles.orderedList}>
            <li>Navigate to <strong>Collection</strong> from the sidebar</li>
            <li>Click the <strong>"Add New Product"</strong> button</li>
            <li>Fill in the product details:
              <ul style={docStyles.nestedList}>
                <li><strong>Title:</strong> Product name (required)</li>
                <li><strong>Price:</strong> Product price in your default currency</li>
                <li><strong>Description:</strong> Detailed product description</li>
                <li><strong>Image URL:</strong> Direct link to product image (must be publicly accessible)</li>
                <li><strong>Product Link:</strong> URL to your website or Instagram post</li>
                <li><strong>Link Type:</strong> Choose between Website or Instagram</li>
                <li><strong>Categories:</strong> Comma-separated list (e.g., "Handwoven, Shawl, Premium")</li>
              </ul>
            </li>
            <li>Click <strong>"Save Product"</strong> to add it to your collection</li>
          </ol>

          <h4 style={docStyles.heading}>Product Types</h4>
          <ul style={docStyles.list}>
            <li><strong>Manual Products:</strong> Added by you through the brand portal (can be deleted)</li>
            <li><strong>Scraped Products:</strong> Automatically imported (can be edited but not deleted)</li>
          </ul>

          <div style={docStyles.tipBox}>
            <strong>💡 Tip:</strong> Use clear, high-quality product images. Images should be at least 800x800 pixels 
            for best display quality. Make sure your image URLs are publicly accessible (not behind authentication).
          </div>
        </div>
      )
    },
    {
      id: 'profile',
      title: 'Profile Management',
      icon: User,
      content: (
        <div>
          <h3 style={docStyles.subtitle}>Managing Your Brand Profile</h3>
          <p style={docStyles.paragraph}>
            Your brand profile contains important information about your brand that appears on the platform.
          </p>

          <h4 style={docStyles.heading}>Editable Fields</h4>
          <ul style={docStyles.list}>
            <li><strong>Contact Name:</strong> Primary contact person for your brand</li>
            <li><strong>Phone:</strong> Contact phone number</li>
            <li><strong>Website:</strong> Your brand's website URL</li>
            <li><strong>Instagram Handle:</strong> Your Instagram username (without @)</li>
            <li><strong>Description:</strong> Brand description and story</li>
            <li><strong>Logo:</strong> Brand logo image URL</li>
            <li><strong>Default Currency:</strong> Currency for pricing your products</li>
            <li><strong>Categories:</strong> Product categories your brand specializes in</li>
          </ul>

          <h4 style={docStyles.heading}>Restricted Fields</h4>
          <p style={docStyles.paragraph}>
            The following fields cannot be changed directly and require contacting support:
          </p>
          <ul style={docStyles.list}>
            <li><strong>Brand Name:</strong> Contact support to change</li>
            <li><strong>Email Address:</strong> Contact support to change</li>
          </ul>
          <p style={docStyles.paragraph}>
            Click the "Contact support to change" link next to these fields to send an email to our support team.
          </p>

          <h4 style={docStyles.heading}>Brand Health Score</h4>
          <p style={docStyles.paragraph}>
            Your brand health score (shown in the sidebar) is calculated based on:
          </p>
          <ul style={docStyles.list}>
            <li><strong>Profile Completeness (25 points):</strong> Logo, description, website, social media, contact info</li>
            <li><strong>Product Quantity (30 points):</strong> Number of products and recent activity</li>
            <li><strong>Product Quality (20 points):</strong> Backend quality assessment</li>
            <li><strong>Engagement (15 points):</strong> Views, saves, and user interactions</li>
            <li><strong>Account Status (10 points):</strong> Active/approved status</li>
          </ul>
          <p style={docStyles.paragraph}>
            <strong>Status Levels:</strong> 80%+ = Excellent, 60%+ = Good standing, 40%+ = Getting started, Below 40% = Setup needed
          </p>
          <p style={docStyles.paragraph}>
            A higher score indicates a more complete and active brand presence on the platform.
          </p>
        </div>
      )
    },
    {
      id: 'security',
      title: 'Account Security',
      icon: Users,
      content: (
        <div>
          <h3 style={docStyles.subtitle}>Keeping Your Account Secure</h3>
          <p style={docStyles.paragraph}>
            Your brand portal account is protected through Supabase authentication. Here's how to manage your account security:
          </p>

          <h4 style={docStyles.heading}>Login & Authentication</h4>
          <ul style={docStyles.list}>
            <li><strong>Secure Login:</strong> Use your email and password to sign in to the brand portal</li>
            <li><strong>Session Management:</strong> Your session remains active for easy access</li>
            <li><strong>Automatic Logout:</strong> You'll be logged out after inactivity for security</li>
          </ul>

          <h4 style={docStyles.heading}>Password Reset</h4>
          <ol style={docStyles.orderedList}>
            <li>On the login page, click <strong>"Forgot your password?"</strong></li>
            <li>Enter your brand email address</li>
            <li>Check your email for a password reset link (check spam folder too)</li>
            <li>Click the link and set a new password (must be 8+ chars with uppercase, lowercase, and number)</li>
            <li>Reset links expire after 1 hour for security</li>
          </ol>

          <h4 style={docStyles.heading}>Security Best Practices</h4>
          <ul style={docStyles.list}>
            <li><strong>Use a strong password:</strong> Mix of uppercase, lowercase, numbers, and special characters</li>
            <li><strong>Don't share credentials:</strong> Keep your login details private</li>
            <li><strong>Log out when done:</strong> Especially on shared computers</li>
            <li><strong>Monitor access:</strong> Regularly check your account for unauthorized activity</li>
          </ul>

          <h4 style={docStyles.heading}>User Avatar</h4>
          <p style={docStyles.paragraph}>
            Your initials appear in the top-right corner of the brand portal as your user avatar. This helps identify your logged-in session at a glance.
          </p>

          <div style={docStyles.tipBox}>
            <strong>💡 Security Tip:</strong> If you notice any suspicious activity or can't access your account, contact support immediately at yemberzal.me@gmail.com or contact@theyemberzal.com
          </div>
        </div>
      )
    },
    {
      id: 'troubleshooting',
      title: 'Troubleshooting',
      icon: HelpCircle,
      content: (
        <div>
          <h3 style={docStyles.subtitle}>Common Issues and Solutions</h3>

          <h4 style={docStyles.heading}>Product Images Not Showing</h4>
          <ul style={docStyles.list}>
            <li>Ensure your image URL is publicly accessible (not behind a login)</li>
            <li>Check that the URL is a direct link to the image file (ends in .jpg, .png, etc.)</li>
            <li>Verify the URL is correct and the image exists</li>
            <li>Try opening the image URL directly in a new browser tab</li>
          </ul>

          <h4 style={docStyles.heading}>Can't Edit or Delete a Product</h4>
          <ul style={docStyles.list}>
            <li>Only manually added products can be deleted</li>
            <li>Scraped products (automatically imported) can be edited but not deleted</li>
            <li>If you need a scraped product removed, contact support</li>
          </ul>

          <h4 style={docStyles.heading}>Analytics Not Updating</h4>
          <ul style={docStyles.list}>
            <li>Analytics data may take a few minutes to update</li>
            <li>Refresh the page if data seems stale</li>
            <li>Ensure you have products with views for analytics to display</li>
          </ul>

          <h4 style={docStyles.heading}>Login Issues</h4>
          <ul style={docStyles.list}>
            <li>Make sure you're using the correct email address</li>
            <li>Check that your password is correct</li>
            <li>If you forgot your password, contact support</li>
            <li>Clear your browser cache and cookies if issues persist</li>
          </ul>

          <h4 style={docStyles.heading}>Need More Help?</h4>
          <p style={docStyles.paragraph}>
            If you're experiencing issues not covered here, please contact our support team:
          </p>
          <p style={docStyles.paragraph}>
            <strong>Email:</strong> <a href="mailto:yemberzal.me@gmail.com" style={docStyles.link}>yemberzal.me@gmail.com</a>
          </p>
          <p style={docStyles.paragraph}>
            <strong>Business:</strong> <a href="mailto:contact@theyemberzal.com" style={docStyles.link}>contact@theyemberzal.com</a>
          </p>
        </div>
      )
    }
  ];

  return (
    <div style={docStyles.container}>
      <div style={docStyles.header}>
        <div>
          <div style={docStyles.label}>D O C U M E N T A T I O N</div>
          <h2 style={docStyles.title}>Brand Portal Guide</h2>
          <p style={docStyles.subtitle}>Everything you need to know about using the brand portal</p>
        </div>
      </div>

      <div style={docStyles.content}>
        {sections.map((section) => {
          const Icon = section.icon;
          const isExpanded = expandedSection === section.id;

          return (
            <div key={section.id} style={docStyles.section}>
              <button
                onClick={() => toggleSection(section.id)}
                style={{
                  ...docStyles.sectionHeader,
                  ...(isExpanded ? docStyles.sectionHeaderActive : {})
                }}
              >
                <div style={docStyles.sectionHeaderContent}>
                  <Icon style={{ width: '20px', height: '20px', marginRight: '12px' }} />
                  <span style={docStyles.sectionTitle}>{section.title}</span>
                </div>
                <ChevronRight
                  style={{
                    width: '20px',
                    height: '20px',
                    transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s ease'
                  }}
                />
              </button>
              
              {isExpanded && (
                <div style={docStyles.sectionContent}>
                  {section.content}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div style={docStyles.footer}>
        <div style={docStyles.footerContent}>
          <div style={docStyles.footerSection}>
            <h4 style={docStyles.footerTitle}>Still have questions?</h4>
            <p style={docStyles.footerText}>
              Our support team is here to help. Reach out to us at{' '}
              <a href="mailto:yemberzal.me@gmail.com" style={docStyles.link}>
                yemberzal.me@gmail.com
              </a>
              <br />
              <a href="mailto:contact@theyemberzal.com" style={docStyles.link}>
                contact@theyemberzal.com
              </a>
            </p>
            {/* <p style={docStyles.footerText}>
              For business inquiries:{' '}
              <a href="mailto:contact@theyemberzal.com" style={docStyles.link}>
                contact@theyemberzal.com
              </a>
            </p> */}
          </div>
          <div style={docStyles.footerSection}>
            <h4 style={docStyles.footerTitle}>Quick Links</h4>
            <ul style={docStyles.footerList}>
              <li><a href="/brand-portal/dashboard" style={docStyles.link}>Dashboard</a></li>
              <li><a href="/brand-portal/collection" style={docStyles.link}>Collection</a></li>
              <li><a href="/brand-portal/analytics" style={docStyles.link}>Analytics</a></li>
              <li><a href="/brand-portal/insights" style={docStyles.link}>Insights</a></li>
              <li><a href="/brand-portal/profile" style={docStyles.link}>Profile</a></li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

const docStyles = {
  container: {
    maxWidth: '900px',
    margin: '0 auto',
  },
  header: {
    marginBottom: '40px',
  },
  label: {
    fontSize: '11px',
    letterSpacing: '0.2em',
    color: '#71717a',
    marginBottom: '8px',
    textTransform: 'uppercase',
  },
  title: {
    fontSize: '32px',
    fontWeight: '600',
    color: '#171717',
    marginBottom: '8px',
    letterSpacing: '-0.02em',
  },
  subtitle: {
    fontSize: '16px',
    color: '#71717a',
    marginBottom: '24px',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  section: {
    background: '#ffffff',
    border: '1px solid #e5e5e5',
    borderRadius: '8px',
    overflow: 'hidden',
    transition: 'all 0.2s ease',
  },
  sectionHeader: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '20px 24px',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    textAlign: 'left',
    transition: 'all 0.2s ease',
  },
  sectionHeaderActive: {
    background: '#fafafa',
    borderBottom: '1px solid #e5e5e5',
  },
  sectionHeaderContent: {
    display: 'flex',
    alignItems: 'center',
  },
  sectionTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#171717',
  },
  sectionContent: {
    padding: '24px',
    background: '#ffffff',
  },
  heading: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#171717',
    marginTop: '24px',
    marginBottom: '12px',
  },
  paragraph: {
    fontSize: '15px',
    lineHeight: '1.6',
    color: '#52525b',
    marginBottom: '16px',
  },
  list: {
    fontSize: '15px',
    lineHeight: '1.8',
    color: '#52525b',
    marginBottom: '16px',
    paddingLeft: '24px',
  },
  orderedList: {
    fontSize: '15px',
    lineHeight: '1.8',
    color: '#52525b',
    marginBottom: '16px',
    paddingLeft: '24px',
  },
  nestedList: {
    marginTop: '8px',
    marginBottom: '8px',
    paddingLeft: '20px',
  },
  tipBox: {
    background: '#f5f5f5',
    borderLeft: '4px solid #171717',
    padding: '16px 20px',
    marginTop: '20px',
    marginBottom: '20px',
    borderRadius: '4px',
    fontSize: '14px',
    lineHeight: '1.6',
    color: '#52525b',
  },
  link: {
    color: '#171717',
    textDecoration: 'underline',
    fontWeight: '500',
  },
  footer: {
    marginTop: '60px',
    paddingTop: '40px',
    borderTop: '1px solid #e5e5e5',
  },
  footerContent: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '40px',
  },
  footerSection: {
    marginBottom: '20px',
  },
  footerTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#171717',
    marginBottom: '12px',
  },
  footerText: {
    fontSize: '14px',
    lineHeight: '1.6',
    color: '#71717a',
  },
  footerList: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
  },
};

export default BrandDocumentation;
